import { Component, ErrorInfo, ReactNode } from 'react';

/**
 * Error boundary to isolate Preview component failures.
 * Prevents the entire page from crashing if the Preview encounters an error.
 */

type Props = { 
  children: ReactNode; 
  fallback?: ReactNode;
};

type State = { 
  hasError: boolean; 
  message?: string;
};

export default class PreviewErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { 
      hasError: true, 
      message: String(error instanceof Error ? error.message : error) 
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // Keep errors confined to this component
    // Log to console for debugging but don't throw
    // eslint-disable-next-line no-console
    console.error('[PreviewPane] error boundary caught:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="flex flex-col items-center justify-center w-full h-full bg-mid-blue/40 border border-white/10 rounded-lg text-light-grey text-sm p-4">
          <div className="mb-2">Preview failed to render</div>
          {this.state.message && (
            <div className="text-xs text-light-grey/60 max-w-md text-center break-words">
              {this.state.message}
            </div>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

